/**
 * FormTabsBar — pasek „Zakładki formularza" buildera modułów własnych i szablonów
 * (hub Krok 8). Parytet wizualny z paskiem zakładek buildera modułów SYSTEMOWYCH.
 *
 * Tryby:
 *   - brak zakładek (legacy, `layout_config` bez `tabs`) — jedna niejawna, nieedytowalna
 *     zakładka „Dane" + przycisk „+ Nowa zakładka" (pierwsze kliknięcie tworzy rejestr:
 *     zakładkę „Dane" z dotychczasowymi sekcjami + nową pustą — robi to strona buildera),
 *   - rejestr zakładek — chipy z tytułami; AKTYWNY chip ma akcje: zmiana nazwy (ołówek →
 *     inline input), przesunięcie ←/→, usunięcie (× — potwierdzenie po stronie strony).
 *
 * Komponent czysto prezentacyjny — stan (tabs/activeTabId) i operacje żyją w
 * ModuleBuilderPage, więc jeden pasek obsługuje oba wcielenia (tenant + hub).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronLeft, ChevronRight, Pencil, Plus, X } from 'lucide-react';

import type { FormLayoutTab } from '@/pages/modules/module-builder-adapter';

/** Slug zakładki z tytułu (deep-linki) — ascii, podkreślniki, unikalny w rejestrze. */
export function uniqueTabSlug(title: string, taken: ReadonlySet<string>): string {
  const base =
    title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ł/g, 'l')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'zakladka';
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
}

interface FormTabsBarProps {
  tabs: FormLayoutTab[];
  /** Aktywna zakładka (id z rejestru). Ignorowane gdy rejestr pusty. */
  activeTabId: string | null;
  onSelect: (tabId: string) => void;
  onAdd: () => void;
  onRename: (tabId: string, title: string) => void;
  onRequestDelete: (tabId: string) => void;
  onMove: (tabId: string, direction: -1 | 1) => void;
}

export function FormTabsBar({
  tabs,
  activeTabId,
  onSelect,
  onAdd,
  onRename,
  onRequestDelete,
  onMove,
}: FormTabsBarProps) {
  const { t } = useTranslation();
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingTabId) inputRef.current?.focus();
  }, [editingTabId]);

  const commitRename = () => {
    if (editingTabId && draftTitle.trim()) onRename(editingTabId, draftTitle.trim());
    setEditingTabId(null);
  };

  const legacy = tabs.length === 0;
  const activeId = legacy ? null : (tabs.find((x) => x.id === activeTabId)?.id ?? tabs[0]!.id);

  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-1.5 rounded-lg border border-border-primary bg-bg-primary px-3 py-2"
      data-testid="form-tabs-bar"
    >
      <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
        {t('tenant.modules.builder.tabs.label')}
      </span>

      {legacy ? (
        <span
          className="inline-flex items-center rounded-md border border-accent-500 bg-accent-500/10 px-3 py-1 text-xs font-medium text-accent-600"
          data-testid="form-tabs-implicit"
        >
          {t('tenant.modules.builder.tabs.defaultFirstTitle')}
        </span>
      ) : (
        tabs.map((tab, idx) => {
          const isActive = tab.id === activeId;
          if (isActive && editingTabId === tab.id) {
            return (
              <span
                key={tab.id}
                className="inline-flex items-center gap-1 rounded-md border border-accent-500 px-2 py-0.5"
              >
                <input
                  ref={inputRef}
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename();
                    if (e.key === 'Escape') {
                      // „Obsłużone" — okno kreatora, w którym stoi pasek zakładek, się nie zamyka.
                      e.preventDefault();
                      setEditingTabId(null);
                    }
                  }}
                  onBlur={commitRename}
                  placeholder={t('tenant.modules.builder.tabs.renamePlaceholder')}
                  className="w-32 bg-transparent text-xs text-text-primary focus:outline-none"
                  data-testid="form-tab-rename-input"
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={commitRename}
                  className="text-accent-600"
                  aria-label={t('common.save')}
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
              </span>
            );
          }
          return (
            <span
              key={tab.id}
              className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium transition ${
                isActive
                  ? 'border-accent-500 bg-accent-500/10 text-accent-600'
                  : 'border-border-primary text-text-secondary hover:border-accent-500/50'
              }`}
              data-testid={`form-tab-${tab.slug}`}
            >
              <button type="button" onClick={() => onSelect(tab.id)} className="px-1 py-0.5">
                {tab.title}
              </button>
              {isActive && (
                <span className="ml-0.5 flex items-center gap-0.5 border-l border-accent-500/30 pl-1">
                  <button
                    type="button"
                    onClick={() => {
                      setDraftTitle(tab.title);
                      setEditingTabId(tab.id);
                    }}
                    className="rounded p-0.5 hover:bg-accent-500/10"
                    aria-label={t('tenant.modules.builder.tabs.rename')}
                    data-testid="form-tab-rename"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  {idx > 0 && (
                    <button
                      type="button"
                      onClick={() => onMove(tab.id, -1)}
                      className="rounded p-0.5 hover:bg-accent-500/10"
                      aria-label={t('tenant.modules.builder.tabs.moveLeft')}
                    >
                      <ChevronLeft className="h-3 w-3" />
                    </button>
                  )}
                  {idx < tabs.length - 1 && (
                    <button
                      type="button"
                      onClick={() => onMove(tab.id, 1)}
                      className="rounded p-0.5 hover:bg-accent-500/10"
                      aria-label={t('tenant.modules.builder.tabs.moveRight')}
                    >
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onRequestDelete(tab.id)}
                    className="rounded p-0.5 text-red-500 hover:bg-red-500/10"
                    aria-label={t('tenant.modules.builder.tabs.delete')}
                    data-testid="form-tab-delete"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
            </span>
          );
        })
      )}

      <button
        type="button"
        onClick={onAdd}
        className="inline-flex items-center gap-1 rounded-md border border-dashed border-border-primary px-2 py-1 text-xs text-text-secondary hover:border-accent-500 hover:text-accent-600"
        data-testid="form-tabs-add"
      >
        <Plus className="h-3 w-3" />
        {t('tenant.modules.builder.tabs.add')}
      </button>
    </div>
  );
}
